import { create } from 'zustand';
import { User, signOut, sendPasswordResetEmail, confirmPasswordReset as firebaseConfirmPasswordReset } from 'firebase/auth';
import { UserData } from '../services/authService';
import { auth } from '../firebase/config';

interface AuthState {
  user: User | null;
  userData: UserData | null;
  isLoading: boolean;
  error: string | null;
  setUser: (user: User | null) => void;
  setUserData: (userData: UserData | null) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<boolean>;
  confirmPasswordReset: (oobCode: string, newPassword: string) => Promise<void>;
  reset: () => void;
}

const useAuthStore = create<AuthState>((set) => ({
  user: null,
  userData: null,
  isLoading: true,
  error: null,
  setUser: (user) => set({ user }),
  setUserData: (userData) => set({ userData }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  logout: async () => {
    set({ isLoading: true, error: null });
    try {
      await signOut(auth);
      // Reset the state upon successful logout
      set({ user: null, userData: null, isLoading: false, error: null });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'حدث خطأ غير متوقع أثناء تسجيل الخروج';
      set({ error: errorMessage, isLoading: false });
    }
  },
  forgotPassword: async (email: string) => {
    set({ isLoading: true, error: null });
    try {
      await sendPasswordResetEmail(auth, email);
      set({ isLoading: false });
      return true;
    } catch (error: unknown) {
      console.error('Password reset error:', error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'حدث خطأ أثناء إرسال بريد إعادة تعيين كلمة المرور';
      set({ error: errorMessage, isLoading: false });
      throw new Error(errorMessage);
    }
  },
  confirmPasswordReset: async (oobCode: string, newPassword: string) => {
    set({ isLoading: true, error: null });
    try {
      await firebaseConfirmPasswordReset(auth, oobCode, newPassword);
      set({ isLoading: false });
    } catch (error: unknown) {
      console.error('Confirm password reset error:', error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'حدث خطأ أثناء تأكيد إعادة تعيين كلمة المرور';
      set({ error: errorMessage, isLoading: false });
      throw new Error(errorMessage);
    }
  },
  reset: () => set({ user: null, userData: null, isLoading: false, error: null }),
}));

export default useAuthStore;
